import { useCallback, useEffect, useRef, useState } from "react";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { storage } from "../helpers/firebaseConfig";

export const DISCLAIMER_STORAGE_PATH = "PodcastMedia/shared/disclaimer.mp3";
export const SPANISH_DISCLAIMER_STORAGE_PATH = "PodcastMedia/shared/disclaimer_ES.mp3";

export function useDisclaimerAudioUrl(languageId = "EN") {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    const storagePath = String(languageId || "EN").toUpperCase() === "ES"
      ? SPANISH_DISCLAIMER_STORAGE_PATH
      : DISCLAIMER_STORAGE_PATH;
    setUrl("");
    getDownloadURL(storageRef(storage, storagePath))
      .then((downloadUrl) => {
        if (!cancelled) setUrl(downloadUrl);
      })
      .catch((error) => {
        console.error("Could not resolve the Cloud Storage disclaimer.", error);
        if (!cancelled) setUrl("");
      });
    return () => { cancelled = true; };
  }, [languageId]);

  return url;
}

export default function useDisclaimerGate(mainMediaRef) {
  const disclaimerAudioRef = useRef(null);
  const disclaimerCompletedRef = useRef(false);
  const [disclaimerPhase, setDisclaimerPhase] = useState("idle");

  const play = useCallback(async ({ restart = false } = {}) => {
    const disclaimer = disclaimerAudioRef.current;
    const mainMedia = mainMediaRef.current;
    if (!mainMedia) throw new Error("Main podcast media is not ready");
    if (!disclaimer?.getAttribute("src")) throw new Error("Disclaimer audio is not ready");

    if (restart) {
      disclaimerCompletedRef.current = false;
      mainMedia.pause();
      mainMedia.currentTime = 0;
      if (disclaimer) disclaimer.currentTime = 0;
    }

    if (!disclaimerCompletedRef.current && disclaimer) {
      await disclaimer.play();
      setDisclaimerPhase("playing");
      return "disclaimer";
    }

    await mainMedia.play();
    setDisclaimerPhase("complete");
    return "main";
  }, [mainMediaRef]);

  const pause = useCallback(() => {
    const disclaimer = disclaimerAudioRef.current;
    if (disclaimer && !disclaimer.paused && !disclaimer.ended) {
      disclaimer.pause();
      setDisclaimerPhase("paused");
      return "disclaimer";
    }
    mainMediaRef.current?.pause();
    return "main";
  }, [mainMediaRef]);

  const handleDisclaimerEnded = useCallback(async () => {
    disclaimerCompletedRef.current = true;
    setDisclaimerPhase("complete");
    const mainMedia = mainMediaRef.current;
    if (!mainMedia) return;
    mainMedia.currentTime = 0;
    try {
      await mainMedia.play();
    } catch (error) {
      setDisclaimerPhase("awaiting-main-play");
      console.warn("Main podcast playback was blocked after the disclaimer.", error);
    }
  }, [mainMediaRef]);

  const reset = useCallback(() => {
    const disclaimer = disclaimerAudioRef.current;
    if (disclaimer) {
      disclaimer.pause();
      disclaimer.currentTime = 0;
    }
    disclaimerCompletedRef.current = false;
    setDisclaimerPhase("idle");
  }, []);

  const gateMainPlayback = useCallback((media) => {
    if (disclaimerCompletedRef.current) return false;
    media?.pause();
    void play().catch((error) => {
      console.warn("Disclaimer playback was blocked.", error);
    });
    return true;
  }, [play]);

  return {
    disclaimerAudioRef,
    disclaimerPhase,
    isDisclaimerPlaying: disclaimerPhase === "playing",
    playWithDisclaimer: play,
    pauseActivePlayback: pause,
    handleDisclaimerEnded,
    resetDisclaimer: reset,
    gateMainPlayback,
  };
}
